import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { FileText, Users, MapPin, Stethoscope, Power } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import { ClinicStatus } from '../types/clinic';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import { getApiErrorMessage } from '@shared/utils/apiError';
import { useFeatureFlags } from '@shared/context/useFeatureFlags';
import ClinicContactsView from './ClinicContactsView';
import ClinicAddressesTab from './ClinicAddressesTab';
import ClinicInfoSection from './ClinicInfoSection';
import ClinicCategoriesSection from './ClinicCategoriesSection';
import ClinicCapabilitiesSection from './ClinicCapabilitiesSection';
import ClinicOnboardingSection from './ClinicOnboardingSection';
import ClinicExternalSystemsSection from './ClinicExternalSystemsSection';
import ClinicNotesSection from './ClinicNotesSection';
import ClinicRelationshipNotesSection from './ClinicRelationshipNotesSection';
import OperatingHoursSection from './OperatingHoursSection';
import ClinicProceduresView from './ClinicProceduresView';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicDetailPanel.css';

type TabType = 'info' | 'contacts' | 'addresses' | 'services';

interface ClinicDetailPanelProps {
  clinicId: number;
  onClose: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

export default function ClinicDetailPanel({
  clinicId,
  onClose,
  activeTab: externalActiveTab,
  onTabChange
}: Readonly<ClinicDetailPanelProps>) {
  const [internalActiveTab, setInternalActiveTab] = useState<TabType>('info');
  const queryClient = useQueryClient();
  const flags = useFeatureFlags();
  // Use external tab if provided, otherwise use internal state.
  // Fall back to 'info' when a caller selects 'contacts' while the flag is
  // off — the Contacts tab button is hidden in that case, so rendering the
  // contacts view anyway would leave the panel with no visible tab content.
  const rawActiveTab = (externalActiveTab as TabType) || internalActiveTab;
  const activeTab: TabType =
    rawActiveTab === 'contacts' && !flags.contacts ? 'info' : rawActiveTab;

  const handleTabChange = (tab: TabType) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  const { data: clinic, isLoading, error } = useQuery({
    queryKey: ['clinic', clinicId],
    queryFn: () => clinicApi.getClinicById(clinicId),
    enabled: clinicId > 0,
  });

  const refetchClinic = () => {
    queryClient.invalidateQueries({ queryKey: ['clinic', clinicId] });
  };

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const statusMutation = useMutation({
    mutationFn: (newStatus: ClinicStatus) => clinicApi.updateStatus(clinicId, newStatus),
    onSuccess: () => {
      setStatusError(null);
      setShowStatusModal(false);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
    },
    onError: (err: unknown) => {
      setStatusError(getApiErrorMessage(err, 'Failed to update clinic status'));
    },
  });

  const openStatusModal = () => {
    setStatusError(null);
    setShowStatusModal(true);
  };

  if (isLoading) {
    return (
      <div className="clinic-detail-panel">
        <div className="detail-panel-loading">
          <div className="loading-spinner"></div>
          <p>Loading clinic details...</p>
        </div>
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="clinic-detail-panel">
        <div className="detail-panel-error">
          <p>{(error as Error)?.message || 'Error loading clinic details'}</p>
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    );
  }

  // SLV2-3195: key badge + toggle off the effective status the API derives,
  // not the legacy isActive bit (which never tracked status changes and left
  // the Activate action unreachable).
  // A Draft clinic has never been active, so it too offers Activate (SLV2-3195 DEV pass 2026-09-14).
  const offersActivate = clinic.status === ClinicStatus.Inactive || clinic.status === ClinicStatus.Draft;

  return (
    <div className="clinic-detail-panel">
      <div className="detail-panel-header">
        <h2 className="detail-panel-title">{clinic.name}</h2>

        <div className="detail-panel-id-status-row">
          <div className="clinic-identifiers">
            <span>ID: {clinic.id}</span>
            {clinic.code && (
              <>
                <span>|</span>
                <span>Code: {clinic.code}</span>
              </>
            )}
          </div>
          <span className={`status-badge ${clinic.statusCssClass}`}>
            {clinic.statusDisplay}
          </span>
          <RequirePermission permission="UserCanEditClinicProfile">
            <button
              type="button"
              className={`detail-panel-status-btn ${offersActivate ? 'detail-panel-status-btn--activate' : 'detail-panel-status-btn--deactivate'}`}
              onClick={openStatusModal}
              disabled={statusMutation.isPending}
            >
              <Power size={14} />
              {offersActivate ? 'Activate' : 'Deactivate'}
            </button>
          </RequirePermission>
        </div>

        {clinic.phone && (
          <div className="detail-panel-phone">{clinic.phone}</div>
        )}

        {(clinic.mailingStreet || clinic.mailingCity) && (
          <div className="detail-panel-address">
            {clinic.mailingStreet && <span>{clinic.mailingStreet}, </span>}
            {clinic.mailingCity && <span>{clinic.mailingCity}</span>}
            {clinic.mailingState && <span>, {clinic.mailingState}</span>}
            {clinic.mailingZip && <span> {clinic.mailingZip}</span>}
          </div>
        )}

        {clinic.preferredMethodOfAppointment && (
          <div className="detail-panel-appt-method">
            {clinic.preferredMethodOfAppointment === 'Walk-in'
              ? 'Walk-ins accepted'
              : `Appointments: ${clinic.preferredMethodOfAppointment}`}
          </div>
        )}

        {clinic.lastVerifiedDate && (
          <div className="detail-panel-verification">
            <div className="verification-label">Last Verified</div>
            <div className="verification-date">
              {new Date(clinic.lastVerifiedDate).toLocaleDateString()}
            </div>
            {clinic.lastVerifiedByUserName && (
              <div className="verification-user">by {clinic.lastVerifiedByUserName}</div>
            )}
          </div>
        )}
      </div>

      <div className="detail-panel-tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => handleTabChange('info')}
        >
          <FileText size={16} />
          <span>Info</span>
        </button>
        {flags.contacts && (
          <button
            type="button"
            className={`tab-btn ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => handleTabChange('contacts')}
          >
            <Users size={16} />
            <span>Contacts</span>
          </button>
        )}
        <button
          type="button"
          className={`tab-btn ${activeTab === 'addresses' ? 'active' : ''}`}
          onClick={() => handleTabChange('addresses')}
        >
          <MapPin size={16} />
          <span>Addresses</span>
        </button>
        <RequirePermission anyOf={['UserCanViewClinicProfile', 'UserCanListClinicProcedures']}>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => handleTabChange('services')}
          >
            <Stethoscope size={16} />
            <span>Services</span>
          </button>
        </RequirePermission>
      </div>

      <div className="detail-panel-content">
        {activeTab === 'info' && (
          <div className="cdp-info-sections">
            <ClinicInfoSection clinic={clinic} onUpdate={refetchClinic} />
            <OperatingHoursSection clinic={clinic} onUpdate={refetchClinic} />
            <ClinicCategoriesSection clinic={clinic} onUpdate={refetchClinic} />
            <ClinicCapabilitiesSection clinic={clinic} onUpdate={refetchClinic} />
            <ClinicOnboardingSection clinic={clinic} onUpdate={refetchClinic} />
            <ClinicExternalSystemsSection clinic={clinic} onUpdate={refetchClinic} />
            <ClinicNotesSection clinic={clinic} />
            <ClinicRelationshipNotesSection clinic={clinic} />
          </div>
        )}
        {activeTab === 'contacts' && <ClinicContactsView clinicId={clinicId} />}
        {activeTab === 'addresses' && <ClinicAddressesTab clinic={clinic} />}
        {activeTab === 'services' && (
          <RequirePermission anyOf={['UserCanViewClinicProfile', 'UserCanListClinicProcedures']}>
            <ClinicProceduresView clinicId={clinicId} />
          </RequirePermission>
        )}
      </div>

      <ConfirmationModal
        isOpen={showStatusModal}
        title={offersActivate ? 'Activate Clinic' : 'Deactivate Clinic'}
        message={
          offersActivate
            ? `Activate "${clinic.name}"? It will be marked Active and ${clinic.status === ClinicStatus.Draft ? 'become available for use' : 'be available for use again'}.`
            : `Deactivate "${clinic.name}"? It will be marked Inactive and will no longer appear as an active clinic. You can reactivate it later from this panel.`
        }
        error={statusError}
        confirmText={offersActivate ? 'Activate' : 'Deactivate'}
        cancelText="Cancel"
        onConfirm={() => statusMutation.mutate(offersActivate ? ClinicStatus.Active : ClinicStatus.Inactive)}
        onCancel={() => { setShowStatusModal(false); setStatusError(null); }}
        danger={!offersActivate}
        loading={statusMutation.isPending}
      />
    </div>
  );
}
